// client/src/pages/InventarioPage.jsx
import React, { useState, useEffect, useRef } from "react";
import { getInventarios, createInventario, updateInventario, deleteInventario } from "../api/inventario.js";
import InputGroup from "../components/InputGroup.js";
import SortableHeader from "../components/SortableHeader.js";
import { useSortableData } from "../hooks/useSortableData.js";
import { usePagination } from "../hooks/usePagination.js";
import { useAuth } from "../context/AuthContext.js";
import Pagination from "../components/Pagination";

const InventarioPage = () => {
  const { usuario: usuarioLogueado } = useAuth();
  const [inventarios, setInventarios] = useState([]);
  const [filteredInventarios, setFilteredInventarios] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const inputRefs = useRef({});

  // Hook de ordenamiento
  const { sortedItems: sortedInventarios, sortConfig, setSortConfig } = useSortableData(filteredInventarios);
  // Hook de paginación
  const { currentPage, setCurrentPage, paginatedItems: currentInventarios, totalPages, itemsPerPage } = usePagination(sortedInventarios, 20);

  const [inventario, setInventario] = useState({
    nombre_insumo: "",
    cantidad: 0,
    unidad: "kg",
    stock_minimo: 0,
    costo_unitario: 0,
    estado: "activo",
    id_restaurante: usuarioLogueado?.id_restaurante || null
  });

  const [errors, setErrors] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // Cargar inventarios
  const loadInventarios = async () => {
    try {
      const data = await getInventarios();
      const list = data.inventarios || [];
      setInventarios(list);
      setFilteredInventarios(list);
    } catch (err) {
      console.error("Error al cargar inventarios:", err);
    }
  };

  useEffect(() => {
    loadInventarios();
  }, []);

  // Filtro general
  useEffect(() => {
    const filtered = inventarios.filter((inv) =>
      Object.values(inv).some((value) =>
        String(value).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
    setFilteredInventarios(filtered);
  }, [searchTerm, inventarios]);

  // Manejar cambios en inputs
  const handleChange = (e) => {
    const { name, value } = e.target;
    setInventario({ ...inventario, [name]: value });
  };

  // Resetear formulario
  const resetForm = () => {
    setInventario({
      nombre_insumo: "",
      cantidad: 0,
      unidad: "kg",
      stock_minimo: 0,
      costo_unitario: 0,
      estado: "activo",
      id_restaurante: usuarioLogueado?.id_restaurante || null
    });
    setEditMode(false);
    setErrors({});
  };

  // Validación
  const validateInventario = (inv) => {
    const errors = {};
    if (!inv.nombre_insumo.trim()) errors.nombre_insumo = "El nombre del insumo es obligatorio";
    if (!inv.unidad.trim()) errors.unidad = "La unidad es obligatoria";
    
    // Validar que los campos numéricos no estén vacíos
    if (inv.cantidad === "" || inv.cantidad === null || inv.cantidad === undefined) {
      errors.cantidad = "La cantidad es obligatoria";
    } else if (parseFloat(inv.cantidad) < 0) {
      errors.cantidad = "La cantidad no puede ser negativa";
    }
    
    if (inv.stock_minimo === "" || inv.stock_minimo === null || inv.stock_minimo === undefined) {
      errors.stock_minimo = "El stock mínimo es obligatorio";
    } else if (parseFloat(inv.stock_minimo) < 0) {
      errors.stock_minimo = "El stock mínimo no puede ser negativo";
    }
    
    if (inv.costo_unitario === "" || inv.costo_unitario === null || inv.costo_unitario === undefined) {
      errors.costo_unitario = "El costo unitario es obligatorio";
    } else if (parseFloat(inv.costo_unitario) < 0) {
      errors.costo_unitario = "El costo unitario no puede ser negativo";
    }
    
    // Validar que la cantidad no sea menor que el stock mínimo
    if (inv.cantidad !== "" && inv.stock_minimo !== "" && 
        parseFloat(inv.cantidad) < parseFloat(inv.stock_minimo)) {
      errors.cantidad = "La cantidad no puede ser menor que el stock mínimo";
    }
    
    return errors;
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateInventario(inventario);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      const firstErrorField = Object.keys(validationErrors)[0];
      const fieldRef = inputRefs.current[firstErrorField];
      if (fieldRef && fieldRef.scrollIntoView) {
        fieldRef.scrollIntoView({ behavior: "smooth", block: "center" });
        fieldRef.focus();
      }
      return;
    }

    try {
      let resultado;
      
      if (editMode) {
        resultado = await updateInventario(inventario.id_inventario, inventario);
        alert("Inventario actualizado correctamente");
        
        // Actualizar el inventario en la lista sin recargar
        setInventarios(prevInventarios => 
          prevInventarios.map(inv => 
            inv.id_inventario === inventario.id_inventario 
              ? { ...inventario, ...resultado.inventario }
              : inv
          )
        );
      } else {
        resultado = await createInventario(inventario);
        alert("Inventario creado correctamente");
        
        // Agregar el nuevo inventario a la lista sin recargar
        setInventarios(prevInventarios => [...prevInventarios, resultado.inventario]);
      }

      resetForm();
      setModalOpen(false);
    } catch (err) {
      let errorMessage = "Error desconocido";

      if (err.response?.data) {
        errorMessage = err.response.data.error || 
                      err.response.data.message || 
                      JSON.stringify(err.response.data);
      } else if (err.message) {
        errorMessage = err.message;
      }

      setErrors({ general: errorMessage });
      alert(errorMessage);
    }
  };

  // Editar inventario
  const handleEdit = (inv) => {
    setInventario({ ...inv });
    setEditMode(true);
    setModalOpen(true);
  };

  // Eliminar inventario
  const handleDelete = async (id) => {
    if (window.confirm("¿Estás seguro de eliminar este insumo del inventario?")) {
      try {
        await deleteInventario(id);
        alert("Inventario eliminado correctamente");
        
        // Eliminar el inventario de la lista sin recargar
        setInventarios(prevInventarios => 
          prevInventarios.filter(inv => inv.id_inventario !== id)
        );
      } catch {
        alert("Error al eliminar inventario");
      }
    }
  };

  // Determinar color según stock
  const getStockColor = (cantidad, stock_minimo) => {
    if (cantidad === 0) return "text-red-600 font-bold";
    if (cantidad <= stock_minimo) return "text-orange-500 font-semibold";
    return "text-green-600";
  };

  return (
    <div className="p-6 max-w-[90rem] mx-auto">
      <h1 className="text-3xl font-bold mb-6 text-center">📦 Gestión de Inventario</h1>

      {/* Buscador + Botón */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <input
          type="text"
          placeholder="🔍 Buscar por cualquier campo..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border p-2 rounded w-full md:w-1/2"
        />
        <button
          onClick={() => {
            resetForm();
            setModalOpen(true);
          }}
          className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700"
        >
          ➕ Nuevo Insumo
        </button>
      </div>

      {/* Tabla */}
      <div className="overflow-x-auto bg-white shadow-md rounded-xl w-full">
        <table className="min-w-full text-sm text-left divide-y divide-gray-200">
          <thead className="bg-gray-100 text-gray-700 font-semibold">
            <tr className="cursor-pointer">
              <SortableHeader label="N" sortKey="id_inventario" sortConfig={sortConfig} setSortConfig={setSortConfig} />
              {usuarioLogueado?.isSuperAdmin && (
                <SortableHeader label="Restaurante" sortKey="restaurante.nombre_comercial" sortConfig={sortConfig} setSortConfig={setSortConfig} />
              )}
              <SortableHeader label="Insumo" sortKey="nombre_insumo" sortConfig={sortConfig} setSortConfig={setSortConfig} />
              <SortableHeader label="Cantidad" sortKey="cantidad" sortConfig={sortConfig} setSortConfig={setSortConfig} />
              <SortableHeader label="Unidad" sortKey="unidad" sortConfig={sortConfig} setSortConfig={setSortConfig} />
              <SortableHeader label="Stock Mínimo" sortKey="stock_minimo" sortConfig={sortConfig} setSortConfig={setSortConfig} />
              <SortableHeader label="Costo Unitario" sortKey="costo_unitario" sortConfig={sortConfig} setSortConfig={setSortConfig} />
              <SortableHeader label="Estado" sortKey="estado" sortConfig={sortConfig} setSortConfig={setSortConfig} />
              <th className="text-center">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {currentInventarios.length > 0 ? (
              currentInventarios.map((inv, idx) => (
                <tr key={inv.id_inventario} className="hover:bg-gray-50">
                  <td className="px-4 py-2">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                  {usuarioLogueado?.isSuperAdmin && (
                    <td className="px-4 py-2">
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                        {inv.restaurante?.nombre_comercial || "Sin restaurante"}
                      </span>
                    </td>
                  )}
                  <td className="px-4 py-2">{inv.nombre_insumo}</td>
                  <td className={`px-4 py-2 ${getStockColor(inv.cantidad, inv.stock_minimo)}`}>
                    {inv.cantidad}
                  </td>
                  <td className="px-4 py-2">{inv.unidad}</td>
                  <td className="px-4 py-2">{inv.stock_minimo}</td>
                  <td className="px-4 py-2">${parseFloat(inv.costo_unitario).toFixed(2)}</td>
                  <td className={`px-4 py-2 text-center font-semibold ${inv.estado === "activo" ? "text-green-600" : "text-red-500"}`}>
                    {inv.estado}
                  </td>
                  <td className="px-4 py-2 text-center flex justify-center gap-2">
                    <button
                      className="bg-yellow-400 text-white px-3 py-1 rounded hover:bg-yellow-500"
                      onClick={() => handleEdit(inv)}
                    >
                      Editar
                    </button>
                    <button
                      className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                      onClick={() => handleDelete(inv.id_inventario)}
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={usuarioLogueado?.isSuperAdmin ? "9" : "8"} className="text-center py-4 text-gray-500">
                  No hay insumos registrados en el inventario
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 relative">
            <button
              onClick={() => setModalOpen(false)}
              className="absolute top-2 right-3 text-gray-500 hover:text-black"
            >
              ✖
            </button>
            <h2 className="text-2xl font-semibold mb-6 text-center sticky top-0 bg-white pb-2">
              {editMode ? "Editar Insumo" : "Nuevo Insumo"}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Mensaje de error general */}
              {errors.general && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700 font-medium">{errors.general}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setErrors({ ...errors, general: null })}
                      className="ml-auto text-red-500 hover:text-red-700"
                    >
                      ✖
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputGroup label="Nombre del Insumo" error={errors.nombre_insumo}>
                  <input
                    name="nombre_insumo"
                    value={inventario.nombre_insumo}
                    onChange={handleChange}
                    placeholder="Ej: Harina, Aceite, etc."
                    className={`input-base ${errors.nombre_insumo ? "border-red-500 focus:ring-red-400" : ""}`}
                    ref={(el) => (inputRefs.current["nombre_insumo"] = el)}
                  />
                </InputGroup>

                <InputGroup label="Unidad de Medida" error={errors.unidad}>
                  <select
                    name="unidad"
                    value={inventario.unidad}
                    onChange={handleChange}
                    className={`input-base ${errors.unidad ? "border-red-500 focus:ring-red-400" : ""}`}
                  >
                    <option value="kg">Kilogramos (kg)</option>
                    <option value="g">Gramos (g)</option>
                    <option value="l">Litros (l)</option>
                    <option value="ml">Mililitros (ml)</option>
                    <option value="unidad">Unidades</option>
                    <option value="caja">Cajas</option>
                    <option value="paquete">Paquetes</option>
                  </select>
                </InputGroup>

                <InputGroup label="Cantidad Actual" error={errors.cantidad}>
                  <input
                    type="number"
                    name="cantidad"
                    value={inventario.cantidad}
                    onChange={handleChange}
                    step="1"
                    min="0"
                    placeholder="0"
                    required
                    className={`input-base ${errors.cantidad ? "border-red-500 focus:ring-red-400" : ""}`}
                    ref={(el) => (inputRefs.current["cantidad"] = el)}
                  />
                </InputGroup>

                <InputGroup label="Stock Mínimo" error={errors.stock_minimo}>
                  <input
                    type="number"
                    name="stock_minimo"
                    value={inventario.stock_minimo}
                    onChange={handleChange}
                    step="1"
                    min="0"
                    placeholder="0"
                    required
                    className={`input-base ${errors.stock_minimo ? "border-red-500 focus:ring-red-400" : ""}`}
                    ref={(el) => (inputRefs.current["stock_minimo"] = el)}
                  />
                </InputGroup>

                <InputGroup label="Costo Unitario ($)" error={errors.costo_unitario}>
                  <input
                    type="number"
                    name="costo_unitario"
                    value={inventario.costo_unitario}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    required
                    className={`input-base ${errors.costo_unitario ? "border-red-500 focus:ring-red-400" : ""}`}
                    ref={(el) => (inputRefs.current["costo_unitario"] = el)}
                  />
                </InputGroup>

                <InputGroup label="Estado" error={errors.estado}>
                  <select
                    name="estado"
                    value={inventario.estado}
                    onChange={handleChange}
                    className={`input-base ${errors.estado ? "border-red-500 focus:ring-red-400" : ""}`}
                  >
                    <option value="activo">Activo</option>
                    <option value="inactivo">Inactivo</option>
                  </select>
                </InputGroup>
              </div>

              {/* Botones */}
              <div className="flex gap-4 justify-center mt-6">
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
                >
                  {editMode ? "Actualizar" : "Crear"}
                </button>
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventarioPage;
